import React, { useState, useEffect, useCallback } from "react";
import { FiCheck, FiX, FiAlertCircle } from "react-icons/fi";

// Type-safe icon wrappers
const CheckIcon = FiCheck as React.FC<React.SVGProps<SVGSVGElement>>;
const CloseIcon = FiX as React.FC<React.SVGProps<SVGSVGElement>>;
const AlertIcon = FiAlertCircle as React.FC<React.SVGProps<SVGSVGElement>>;

export interface ToastData {
  id: string;
  type: "success" | "error";
  title: string;
  message?: string;
}

interface ToastProps {
  toast: ToastData;
  onRemove: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({ toast, onRemove }) => {
  const [isExiting, setIsExiting] = useState(false);

  const handleRemove = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      onRemove(toast.id);
    }, 300);
  }, [toast.id, onRemove]);

  useEffect(() => {
    const timer = setTimeout(() => {
      handleRemove();
    }, 4000);

    return () => clearTimeout(timer);
  }, [handleRemove]);

  return (
    <div
      className={`su-toast su-toast-${toast.type} ${isExiting ? "su-toast-exiting" : ""}`}
    >
      <div className="su-toast-icon">
        {toast.type === "success" ? (
          <CheckIcon className="w-5 h-5" />
        ) : (
          <AlertIcon className="w-5 h-5" />
        )}
      </div>
      <div className="su-toast-content">
        <div className="su-toast-title">{toast.title}</div>
        {toast.message && <div className="su-toast-message">{toast.message}</div>}
      </div>
      <button className="su-toast-close" onClick={handleRemove}>
        <CloseIcon className="w-4 h-4" />
      </button>
    </div>
  );
};

// Toast Container Component
interface ToastContainerProps {
  toasts: ToastData[];
  onRemove: (id: string) => void;
}

export const ToastContainer: React.FC<ToastContainerProps> = ({
  toasts,
  onRemove,
}) => {
  if (toasts.length === 0) return null;

  return (
    <div className="su-toast-container">
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onRemove={onRemove} />
      ))}
    </div>
  );
};

// Toast hook for easy usage
export const useToast = () => {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const addToast = useCallback(
    (type: "success" | "error", title: string, message?: string) => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      setToasts((prev) => [...prev, { id, type, title, message }]);
    },
    []
  );

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const success = useCallback(
    (title: string, message?: string) => {
      addToast("success", title, message);
    },
    [addToast]
  );

  const error = useCallback(
    (title: string, message?: string) => {
      addToast("error", title, message);
    },
    [addToast]
  );

  return {
    toasts,
    removeToast,
    success,
    error,
  };
};

export default Toast;


